import { ReactNode, useEffect } from 'react';

import { SlideshowSlide, ViewProps } from './slideshow.constants';
import PagesPreviewPagination from '../shared/pages-preview-pagination';
import { PagesActions } from '../shared/pages-preview.style';
import { ComponentAbsoluteWrapper } from '../shared/component-height/component-dimensions.style';
import { ComponentAspectRatio } from '../shared/component-height/component-dimensions';
import { ComponentImage } from '../component-image';
import { useCommonSlideshow } from './slideshow.hooks';
import { ImageViewFill } from '../image/image-view';
import {
  SlideshowCaption,
  SlideshowPageControls,
  SlideshowPages,
  SlideshowWrapper,
} from './slideshow-view.style';
import { ComponentCaptionEdit } from '../shared/component-caption/component-caption-edit';
import { useBatchComponentUpdateContext } from '../shared/batch-component-update.context';
import { SlideshowEditContainer } from './slideshow-edit.style';

const SlideshowEditPreview = ({
  slideshowImages,
  renderActions,
  properties,
}: ViewProps & {
  renderActions: (slide: SlideshowSlide) => ReactNode;
}) => {
  const {
    slides,
    imageDimensions,
    index,
    setIndex,
    isLoading,
    getPrev,
    getNext,
    setContainerElement,
  } = useCommonSlideshow({
    slideshowImages,
    selectedIndex: 0,
    captureKeypress: false,
  });

  const slide: SlideshowSlide | undefined = slides[index];

  const { updateComponentProperties } = useBatchComponentUpdateContext();

  useEffect(() => {
    // reset index when removing items
    if (index >= slides.length) {
      setIndex(slides.length - 1);
    }
  }, [index, slides, setIndex]);

  return (
    <ComponentAspectRatio dimensions={properties.dimensionData}>
      <ComponentAbsoluteWrapper $centred={false}>
        <SlideshowEditContainer ref={setContainerElement}>
          <SlideshowWrapper>
            <PagesActions>{renderActions(slide)}</PagesActions>
            <SlideshowPages $isLoading={isLoading}>
              {slide && !isLoading && (
                <>
                  {!slide.properties.cropData ? (
                    <ComponentImage
                      images={slide.properties.imageUrls || []}
                      altText={slide.properties.altText || ''}
                      imageDimensions={imageDimensions}
                    />
                  ) : (
                    <ImageViewFill
                      imageUrls={slide.properties.imageUrls || []}
                      altText={slide.properties.altText}
                    />
                  )}
                </>
              )}
            </SlideshowPages>
            {slide && properties.showSlideCaptionOnLesson && (
              <SlideshowCaption>
                <ComponentCaptionEdit
                  key={slide.id}
                  caption={slide.properties.caption}
                  onChange={caption => {
                    updateComponentProperties({
                      input: {
                        sectionId: slide.sectionId,
                        componentId: slide.id,
                        componentData: {
                          properties: {
                            showCaptionOnLesson:
                              properties.showSlideCaptionOnLesson,
                            caption,
                          },
                        },
                      },
                      properties: {
                        ...slide.properties,
                        caption,
                      },
                      typename: 'ActivitiesImageComponent',
                    });
                  }}
                />
              </SlideshowCaption>
            )}
          </SlideshowWrapper>
          <PagesPreviewPagination
            currentPage={index}
            totalPageCount={slides.length}
            allyName={`Slideshow: ${slides.length} images`}
            onClick={getPrev}
          />
          <PagesPreviewPagination
            isNext
            currentPage={index}
            totalPageCount={slides.length}
            allyName={`Slideshow: ${slides.length} images`}
            onClick={getNext}
          />
          <SlideshowPageControls>
            <span>{`Page ${index + 1} of ${slides.length}`}</span>
          </SlideshowPageControls>
        </SlideshowEditContainer>
      </ComponentAbsoluteWrapper>
    </ComponentAspectRatio>
  );
};

export default SlideshowEditPreview;
